'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { AlertCircle, CheckCircle2, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { regionalizePath } from '@/lib/config/branches-config';
import { useLanguage } from '@/lib/config/useLanguage';
import { buildLeadThankYouUrl, clearPendingLeadSubmissionId, extractOptionalLeadId, getOrCreatePendingLeadSubmissionId } from '@/lib/forms/lead-thank-you';
import { TRUSS_CALCULATOR_OPTIONS } from '@/components/truss-calculation/config';
import { useInvisibleSmartCaptcha } from '@/components/SmartCaptcha';

import { trackLeadFormStage, trackLeadFormSuccess } from '@/lib/analytics/metrika';
import { useLeadFormAnalytics } from '@/lib/analytics/use-lead-form-analytics';

const FORM_TYPE = 'truss-calculation';

const { trussTypes, materials, spans, coatings, quantities } = TRUSS_CALCULATOR_OPTIONS;

const INITIAL_QUIZ = {
  trussType: '',
  material: '',
  span: '',
  spanCustom: '',
  coating: '',
  quantity: '',
  quantityCustom: '',
};

const INITIAL_CONTACT = {
  name: '',
  phone: '',
  email: '',
  message: '',
  privacy: false,
  marketing: false,
};

export default function TrussCalculationLeadForm({
  title,
  description,
  notice,
  variant = 'default',
}: {
  title: string;
  description: string;
  notice: string;
  variant?: 'default' | 'hero';
}) {
  const router = useRouter();
  const analytics = useLeadFormAnalytics(FORM_TYPE);
  const { localizePath, langCode } = useLanguage();
  const privacyPolicyUrl = regionalizePath(localizePath('/pages/politika-konfidentsialnosti-personalnykh-dannykh'), 'spb');
  const [quizData, setQuizData] = useState(INITIAL_QUIZ);
  const [contactData, setContactData] = useState(INITIAL_CONTACT);
  const [contactError, setContactError] = useState('');
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { captchaElement, executeCaptcha, resetCaptcha } = useInvisibleSmartCaptcha();

  const isHeroVariant = variant === 'hero';
  const fieldClassName =
    'mt-1.5 flex h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-base text-slate-900 shadow-sm placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-950 md:mt-2 md:text-sm';
  const textareaClassName =
    'mt-1.5 flex w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-base text-slate-900 shadow-sm transition-colors placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-950 md:mt-2 md:text-sm';
  const selectTriggerClassName = isHeroVariant
    ? 'mt-1.5 h-11 rounded-xl border-white/15 bg-white text-base text-slate-900 data-[placeholder]:text-slate-500 md:mt-2 md:text-sm'
    : 'mt-1.5 h-11 rounded-xl border-slate-200 bg-white text-base text-slate-900 md:mt-2 md:text-sm';

  const updateQuizField = (field: keyof typeof INITIAL_QUIZ, value: string) => {
    setContactError('');
    setSubmitStatus('idle');
    setQuizData((prev) => ({ ...prev, [field]: value }));
  };

  const updateContactField = (field: keyof typeof INITIAL_CONTACT, value: string | boolean) => {
    setContactError('');
    setSubmitStatus('idle');
    setContactData((prev) => ({ ...prev, [field]: value }));
  };

  const getOptionLabel = (
    options: Array<{ value: string; label: string }>,
    value: string,
    customValue?: string,
  ) => {
    if (value === 'custom') {
      return customValue?.trim() || 'Не указано';
    }

    return options.find((option) => option.value === value)?.label || 'Не указано';
  };

  const renderSelectOptions = (options: Array<{ value: string; label: string }>) =>
    options.map((option) => (
      <SelectItem key={option.value} value={option.value}>
        {option.label}
      </SelectItem>
    ));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setContactError('');
    setSubmitStatus('idle');

    if (!quizData.trussType) {
      setContactError('Выберите желательный тип ферм.');
      return;
    }

    if (!quizData.material) {
      setContactError('Выберите предпочтительный материал.');
      return;
    }

    if (!quizData.span) {
      setContactError('Укажите длину пролёта.');
      return;
    }

    if (quizData.span === 'custom' && !quizData.spanCustom.trim()) {
      setContactError('Укажите свой вариант длины пролёта.');
      return;
    }

    if (!quizData.coating) {
      setContactError('Выберите вариант покрытия.');
      return;
    }

    if (!quizData.quantity) {
      setContactError('Укажите количество ферм.');
      return;
    }

    if (quizData.quantity === 'custom' && !quizData.quantityCustom.trim()) {
      setContactError('Укажите свой вариант количества ферм.');
      return;
    }

    if (!contactData.name.trim()) {
      setContactError('Укажите имя для отправки заявки.');
      return;
    }

    if (!contactData.phone.trim()) {
      setContactError('Заполните телефон для отправки заявки.');
      return;
    }

    if (!contactData.privacy) {
      setContactError('Необходимо согласие на обработку персональных данных.');
      return;
    }

    setIsSubmitting(true);

    const message = [
      'Заявка на расчёт ферм',
      '',
      `Тип ферм: ${getOptionLabel(trussTypes, quizData.trussType)}`,
      `Материал: ${getOptionLabel(materials, quizData.material)}`,
      `Пролёт: ${getOptionLabel(spans, quizData.span, quizData.spanCustom)}`,
      `Покрытие: ${getOptionLabel(coatings, quizData.coating)}`,
      `Количество: ${getOptionLabel(quantities, quizData.quantity, quizData.quantityCustom)}`,
      '',
      `Имя: ${contactData.name.trim() || 'Не указано'}`,
      `Email: ${contactData.email.trim() || 'Не указан'}`,
      '',
      'Комментарий клиента:',
      contactData.message.trim() || 'Без дополнительного комментария.',
    ].join('\n');

    try {
      const submissionId = getOrCreatePendingLeadSubmissionId(FORM_TYPE, window.location.pathname);
      const smartToken = await executeCaptcha();
      const response = await fetch('/api/forms', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: contactData.name,
          phone: contactData.phone,
          email: contactData.email,
          message,
          privacy: contactData.privacy,
          marketing: contactData.marketing,
          formType: FORM_TYPE,
          pageUrl: typeof window !== 'undefined' ? window.location.href : '',
          locale: langCode,
          smartToken,
          submissionId,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        trackLeadFormSuccess(FORM_TYPE, submissionId);
        const thankYouUrl = buildLeadThankYouUrl({
          pathname: window.location.pathname,
          search: window.location.search,
          formType: FORM_TYPE,
          leadId: extractOptionalLeadId(data),
          sid: submissionId,
        });

        clearPendingLeadSubmissionId(FORM_TYPE, window.location.pathname);
        router.push(thankYouUrl);
        return;
      } else {
        trackLeadFormStage(FORM_TYPE, 'error', 'server');
        setSubmitStatus('error');
        setSubmitMessage(data.message || 'Ошибка при отправке заявки.');
      }
    } catch (error) {
      trackLeadFormStage(FORM_TYPE, 'error', 'network-or-captcha');
      setSubmitStatus('error');
      setSubmitMessage('Не удалось отправить заявку. Попробуйте ещё раз.');
    } finally {
      resetCaptcha();
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="calculator"
      className={
        isHeroVariant
          ? 'rounded-[24px] border border-white/15 bg-white/10 p-3.5 backdrop-blur-sm md:rounded-[28px] md:p-6'
          : 'mt-8 rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm md:mt-10 md:rounded-[32px] md:p-8'
      }
    >
      <div>
        {!isHeroVariant && (
          <>
            <div className="inline-flex rounded-full bg-red-50 px-3 py-1 text-sm font-medium text-red-600">
              Заявка на расчёт ферм
            </div>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight text-slate-900">{title}</h2>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-600">{description}</p>
            <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              {notice}
            </div>
          </>
        )}

        <form {...analytics} onSubmit={handleSubmit} className={`mt-5 space-y-4 md:mt-8 md:space-y-6 ${isHeroVariant ? 'text-white' : ''}`}>
          <div className="grid gap-3 md:gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <Label htmlFor="trussTypeSelect">
                1. Желательный тип ферм <span className="text-red-500">*</span>
              </Label>
              <Select value={quizData.trussType} onValueChange={(value) => updateQuizField('trussType', value)}>
                <SelectTrigger id="trussTypeSelect" className={selectTriggerClassName}>
                  <SelectValue placeholder="Выберите тип ферм" />
                </SelectTrigger>
                <SelectContent>{renderSelectOptions(trussTypes)}</SelectContent>
              </Select>
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="materialSelect">
                2. Предпочтительный материал <span className="text-red-500">*</span>
              </Label>
              <Select value={quizData.material} onValueChange={(value) => updateQuizField('material', value)}>
                <SelectTrigger id="materialSelect" className={selectTriggerClassName}>
                  <SelectValue placeholder="Выберите материал" />
                </SelectTrigger>
                <SelectContent>{renderSelectOptions(materials)}</SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="spanSelect">
                3. Сколько метров пролёт? <span className="text-red-500">*</span>
              </Label>
              <Select value={quizData.span} onValueChange={(value) => updateQuizField('span', value)}>
                <SelectTrigger id="spanSelect" className={selectTriggerClassName}>
                  <SelectValue placeholder="Выберите пролёт" />
                </SelectTrigger>
                <SelectContent>{renderSelectOptions(spans)}</SelectContent>
              </Select>
              {quizData.span === 'custom' && (
                <div className="mt-3 md:mt-4">
                  <Label htmlFor="spanCustom">
                    Укажите пролёт <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="spanCustom"
                    value={quizData.spanCustom}
                    onChange={(e) => updateQuizField('spanCustom', e.target.value)}
                    className={fieldClassName}
                    placeholder="Например, 18 м"
                  />
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="coatingSelect">
                4. Какое покрытие (окраска)? <span className="text-red-500">*</span>
              </Label>
              <Select value={quizData.coating} onValueChange={(value) => updateQuizField('coating', value)}>
                <SelectTrigger id="coatingSelect" className={selectTriggerClassName}>
                  <SelectValue placeholder="Выберите покрытие" />
                </SelectTrigger>
                <SelectContent>{renderSelectOptions(coatings)}</SelectContent>
              </Select>
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="quantitySelect">
                5. Сколько нужно ферм? <span className="text-red-500">*</span>
              </Label>
              <Select value={quizData.quantity} onValueChange={(value) => updateQuizField('quantity', value)}>
                <SelectTrigger id="quantitySelect" className={selectTriggerClassName}>
                  <SelectValue placeholder="Выберите количество" />
                </SelectTrigger>
                <SelectContent>{renderSelectOptions(quantities)}</SelectContent>
              </Select>
              {quizData.quantity === 'custom' && (
                <div className="mt-3 md:mt-4">
                  <Label htmlFor="quantityCustom">
                    Укажите количество <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="quantityCustom"
                    value={quizData.quantityCustom}
                    onChange={(e) => updateQuizField('quantityCustom', e.target.value)}
                    className={fieldClassName}
                    placeholder="Например, 25 шт"
                  />
                </div>
              )}
            </div>
          </div>

          <div className="grid gap-4 md:gap-6 md:grid-cols-2">
            <div>
              <Label htmlFor="name">
                Имя <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                value={contactData.name}
                onChange={(e) => updateContactField('name', e.target.value)}
                className={fieldClassName}
                placeholder="Иван"
                required
              />
            </div>
            <div>
              <Label htmlFor="phone">
                Телефон <span className="text-red-500">*</span>
              </Label>
              <Input
                id="phone"
                value={contactData.phone}
                onChange={(e) => updateContactField('phone', e.target.value)}
                className={fieldClassName}
                placeholder="+7 (999) 123-45-67"
                required
              />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={contactData.email}
                onChange={(e) => updateContactField('email', e.target.value)}
                className={fieldClassName}
                placeholder="example@mail.ru"
              />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="message">Комментарий</Label>
              <textarea
                id="message"
                value={contactData.message}
                onChange={(e) => updateContactField('message', e.target.value)}
                rows={4}
                className={textareaClassName}
                placeholder="Опишите объект, нагрузки, сроки или любые пожелания по ферме."
              />
            </div>
          </div>

          <div className="space-y-3 md:space-y-4">
            <div className="flex items-start">
              <Checkbox
                id="privacy"
                checked={contactData.privacy}
                onCheckedChange={(checked) => updateContactField('privacy', checked === true)}
                className={isHeroVariant ? 'mt-1 border-white bg-white text-slate-900' : 'mt-1'}
              />
              <Label htmlFor="privacy" className={`ml-2 text-sm leading-5 md:leading-6 ${isHeroVariant ? 'text-white/75' : 'text-slate-600'}`}>
                Я даю согласие на обработку персональных данных и соглашаюсь с{' '}
                <a href={privacyPolicyUrl} className="text-red-600 hover:underline">
                  политикой конфиденциальности
                </a>
                .
              </Label>
            </div>
            <div className="flex items-start">
              <Checkbox
                id="marketing"
                checked={contactData.marketing}
                onCheckedChange={(checked) => updateContactField('marketing', checked === true)}
                className={isHeroVariant ? 'mt-1 border-white bg-white text-slate-900' : 'mt-1'}
              />
              <Label htmlFor="marketing" className={`ml-2 text-sm leading-5 md:leading-6 ${isHeroVariant ? 'text-white/75' : 'text-slate-600'}`}>
                Разрешаю использовать мои контакты для уточнения заявки и коммерческого предложения.
              </Label>
            </div>
          </div>

          {contactError && (
            <div className="flex items-center gap-2 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {contactError}
            </div>
          )}

          {submitStatus === 'success' && (
            <div className="flex items-center gap-2 rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
              <CheckCircle2 className="h-4 w-4 shrink-0" />
              {submitMessage}
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="flex items-center gap-2 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {submitMessage}
            </div>
          )}

          {captchaElement}

          <div className="flex justify-end">
            <Button type="submit" className="bg-red-600 text-white hover:bg-red-700" disabled={isSubmitting}>
              {isSubmitting ? 'Отправка...' : 'Отправить заявку'}
              {!isSubmitting && <Send className="h-4 w-4" />}
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
}
